import { z } from 'zod'
import { DateStringSchema, FlightHoursSchema, RegistrationSchema, TimeStringSchema } from './booking'

// An instructor's availability (issue #253): the windows in which students may
// book a lesson with them. One-off, or repeating weekly.

export const CreateAvailabilitySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('window'),
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    note: z.string().trim().max(255).nullable().optional(),
  }),
  z.object({
    kind: z.literal('weekly'),
    // ISO weekday: 1 = Monday ... 7 = Sunday.
    weekday: z.number().int().min(1).max(7),
    startTime: TimeStringSchema,
    endTime: TimeStringSchema,
    validFrom: DateStringSchema,
    validTo: DateStringSchema.nullable().optional(),
    note: z.string().trim().max(255).nullable().optional(),
  }),
])
export type CreateAvailabilityInput = z.infer<typeof CreateAvailabilitySchema>

// Several windows or rules at once, in one transaction: all or none, and what
// is already offered is skipped rather than doubled.
export const CreateAvailabilityBatchSchema = z.object({
  items: z.array(CreateAvailabilitySchema).min(1).max(14),
})
export type CreateAvailabilityBatchInput = z.infer<typeof CreateAvailabilityBatchSchema>

// Open booking: students may book the instructor at any time they are not
// already booked, instead of inside published availability.
export const AvailabilityModeSchema = z.object({ openBooking: z.boolean() })
export type AvailabilityModeInput = z.infer<typeof AvailabilityModeSchema>

export interface AvailabilityWindow {
  availabilityId: string
  startsAt: string
  endsAt: string
  note: string | null
}

export interface AvailabilityRule {
  ruleId: string
  weekday: number
  startTime: string
  endTime: string
  validFrom: string
  validTo: string | null
  note: string | null
}

export interface InstructorAvailability {
  windows: AvailabilityWindow[]
  rules: AvailabilityRule[]
  openBooking: boolean
  // Whether students are held to it now: not in open booking, and something
  // upcoming is published. False means any free time can be booked.
  enforced: boolean
  // Windows and rules together, for [from, to), before any booking is taken out.
  expanded: { startsAt: string; endsAt: string }[]
}

// The booking wizard's question: when can this lesson be flown next? The
// instructor is the student's own unless the caller names one (a student with
// none yet, an administrator, or an instructor who is not the student's usual).
export const SuggestLessonSlotsSchema = z.object({
  enrolmentId: z.number().int().positive(),
  instructorUserId: z.string().min(1).optional(),
  // The whole booking, briefing and debriefing included.
  durationMinutes: z.number().int().min(30).max(480).default(90),
  estimatedFlightHours: FlightHoursSchema.optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  limit: z.number().int().min(1).max(24).default(12),
  registration: RegistrationSchema.optional(),
})
export type SuggestLessonSlotsInput = z.infer<typeof SuggestLessonSlotsSchema>

export interface LessonSlot {
  startsAt: string
  endsAt: string
  registration: string
  // Every aircraft free for the slot, the suggested one first.
  registrations: string[]
}

export interface SuggestLessonSlotsResult {
  instructor: { userId: string; displayName: string } | null
  // Who teaches the course, for choosing when there is no usual instructor.
  candidates: { userId: string; displayName: string }[]
  // Whether the instructor has published any availability; without it there is
  // nothing to suggest.
  hasAvailability: boolean
  estimatedFlightHours: number
  // The student's own aircraft and their favourite, when they have chosen
  // any; slots show which of them are free. Empty: the whole fleet is offered.
  fleet: string[]
  preferred: string | null
  // Of those, the aircraft the instructor has no type rating for: never free.
  unrated: string[]
  slots: LessonSlot[]
}

// The aircraft a member can fly (issue #253). The favourite only means
// something among several; the booking wizard offers these, it first.
export const MyAircraftSchema = z.object({
  registrations: z.array(RegistrationSchema).max(40),
  preferred: RegistrationSchema.nullable(),
})
export type MyAircraftInput = z.infer<typeof MyAircraftSchema>

export interface MyAircraft {
  registrations: string[]
  preferred: string | null
}
